<template lang="pug">
  .order-status
    h3.rss-block-header {{ __('Status') }}
    estimated-delivery(
      :current-package="currentPackage"
      :delivery-address="deliveryAddress"
      :email="email"
    )
    .shipping-container(v-if="!currentPackage.isPickup")
      .shipping-method(v-if="currentPackage.canShowAsShipped")
        .icon-carrier(v-if="carrierIcon")
          Icon.icon-size-xxl(:name="carrierIcon")
        .shipping-method-text
          .carrier {{ __('Shipped') }} {{ currentPackageShippingMethod }}
          .tracking {{ __('Tracking: ') }}
            a(
              target="_blank"
              rel="noopener noreferrer"
              v-if="currentPackage.carrierUrl"
              :href="currentPackage.carrierUrl"
            ) {{ trackingNumber || 'N/A' }}
            span(v-if="!currentPackage.carrierUrl") {{ trackingNumber || 'N/A' }}
      .no-shipping-method(v-else)
        span {{ __('Shipping and Tracking Info Pending') }}
    .track-with-app(v-if="!$rogue.appContext")
      .track-with-app-description
        img.icon-phones(:src="iconPhones" alt="Phone Icon" )
        h4.label {{ __('Track your order via our mobile app') }}
      .badges
        a(href="https://itunes.apple.com/us/app/rogue-app/id1106322870?ls=1&mt=8" rel="noopener noreferrer" target="_blank")
          img.apple-store(:src="badgeAppleStore" alt="Apple Store" )
        a(href="https://play.google.com/store/apps/details?id=com.roguefitness.app" rel="noopener noreferrer" target="_blank")
          img.android-store(:src="badgeAndroidStore" alt="Android Store" )
    .rss-feedback
      .feedback-img
        Icon.icon-size-xxl(name="rss/feedback")
      .feedback-area
        span.font-size-xxs {{ __('Thank you for using the Rogue Shipping System. We value your opinion, ' ) }}
        rogue-link(
          to="/rss-feedback"
          rel="noopener noreferrer"
        ) {{ __('please tell us about your experience.') }}
</template>

<script setup lang="ts">
import { computed } from 'vue'
import iconPhones        from '../assets/icon-phones.png'
import badgeAppleStore   from '../assets/badge-apple-store.svg'
import badgeAndroidStore from '../assets/badge-android-store.svg'
import type { IRssPackage } from '../rssTypes'
import estimatedDelivery from './estimated-delivery.vue'

const carrierIcons: Record<string, string> = {
  UPS           : 'ups',
  UPSM          : 'ups',
  UPSF          : 'ups',
  USPS          : 'usps',
  'USPS-POSTAL' : 'usps',
  FEDEX         : 'fedex',
  FX            : 'fedex',
  FDXG          : 'fedex',
  FDXE          : 'fedex',
  PTNL          : 'postnl',
  DBS           : 'dbschenker',
  STRK          : 'startrack',
  AUSP          : 'aus',
}

const props = withDefaults(defineProps<{
  currentPackage: Partial<IRssPackage>,
  deliveryAddress?: object | string,
  email?: string,
}>(), {
  deliveryAddress : () => ({}),
  email           : '',
})

// Get the carrier icon
const carrierIcon = computed<string | null>(() => {
  const icon = carrierIcons[props.currentPackage?.carrier as string]

  return icon ? `carriers/${icon}` : null
})

// Return current package shipping method
const currentPackageShippingMethod = computed<string>(() => {
  const { isExpedited, shippingMethod, carrier } = props.currentPackage

  if (
    isExpedited
    && shippingMethod
    && carrier
  ) {
    return isExpedited ? shippingMethod : carrier
  }

  return ''
})

const trackingNumber = computed<string | undefined>(() => {
  return props.currentPackage?.trackingNumber?.[0]
})
</script>

<style lang="scss" scoped>
  .order-status {
    background-color : $color-white;
    padding          : $space-s;
    height           : 100%;
  }

  .rss-block-header {
    margin-bottom  : 0;
    font-size      : $size-m;
    padding-bottom : $space-xxs;
    border-bottom  : $border-width-thin solid $color-gray-light;
    text-align     : center;

    @media(min-width: $desktop) {
      text-align: initial;
    }
  }

  .shipping-method, .no-shipping-method, .track-with-app {
    padding       : $space-s;
    border-bottom : $border-width-thin solid $color-gray-light;
  }

  .shipping-method {
    display         : flex;
    justify-content : center;
    align-items     : center;

    .icon-carrier {
      padding-right : $space-xxxs;
    }

    .shipping-method-text {
      font-family : $font-condensed;
      font-size   : $size-xxs;
      text-align  : left;
    }
  }

  .no-shipping-method {
    text-align  : center;
    font-family : $font-condensed;
    font-size   : $size-xs;
  }

  .rss-feedback {
    display     : flex;
    padding     : $space-s 0 0 $space-xxs;
    line-height : $line-height-s;
    align-items : center;

    .feedback-area {
      margin-left : $space-s;
      font-size   : $size-xxs;
    }
  }

  .track-with-app {
    display         : flex;
    justify-content : space-between;
    align-items     : center;

    .track-with-app-description {
      display     : flex;
      align-items : center;

      .icon-phones {
        width     : $size-xl;
        min-width : $size-xl;
      }

      .label {
        font-size     : $size-xs;
        margin-bottom : 0;
        margin-right  : $space-xs;
        margin-left   : $space-xs;
      }
    }

    .badges {
      display         : flex;
      justify-content : space-between;
      align-items     : center;
      flex-direction  : column;

      a {
        display     : inline-block;
        margin-left : $space-xxs;

        .apple-store, .android-store {
          max-width : 100%;
          display   : inline-block;
          min-width : 80px;

          @media (min-width : $desktop) {
            min-width : 65px;
          }
        }
      }

      @media (min-width : $mobile) {
        flex-direction: initial;
      }
    }
  }
</style>
